import { store } from './store';

const loadedGroups = new Map();

const inject = (key, saga) => store.injectSaga(key, saga);

const sagaGroups = {
  listing: async () => {
    const { watchShop } = await import('./saga/listingSaga');
    inject('listing/watchShop', watchShop);
  },
  product: async () => {
    const [detail, cart, wishlist, recentlyViewed] = await Promise.all([
      import('./saga/detailSaga'),
      import('./saga/cartSaga'),
      import('./saga/wishlistSaga'),
      import('./saga/recentlyViewedSliceSaga'),
    ]);
    inject('product/watchShopDetail', detail.watchShopDetail);
    inject('product/watchShopDetailReset', detail.watchShopDetailReset);
    inject('cart/watchAddToCart', cart.watchAddToCart);
    inject('cart/watchRemoveFromCart', cart.watchRemoveFromCart);
    inject('cart/watchCartList', cart.watchCartList);
    inject('cart/watchUpdateCartList', cart.watchUpdateCartList);
    inject('cart/watchApplyVoucher', cart.watchApplyVoucher);
    inject('cart/watchRemoveVoucher', cart.watchRemoveVoucher);
    inject('wishlist/watchAddToWishlist', wishlist.watchAddToWishlist);
    inject('wishlist/watchUpdateWishlist', wishlist.watchUpdateWishlist);
    inject('product/recentlyViewed', recentlyViewed.default);
  },
  cart: async () => {
    const [cart, wishlist] = await Promise.all([
      import('./saga/cartSaga'),
      import('./saga/wishlistSaga'),
    ]);
    inject('cart/watchAddToCart', cart.watchAddToCart);
    inject('cart/watchRemoveFromCart', cart.watchRemoveFromCart);
    inject('cart/watchCartList', cart.watchCartList);
    inject('cart/watchUpdateCartList', cart.watchUpdateCartList);
    inject('cart/watchApplyVoucher', cart.watchApplyVoucher);
    inject('cart/watchRemoveVoucher', cart.watchRemoveVoucher);
    inject('wishlist/watchAddToWishlist', wishlist.watchAddToWishlist);
    inject('wishlist/watchUpdateWishlist', wishlist.watchUpdateWishlist);
  },
  checkout: async () => {
    await sagaGroups.cart();
    const checkout = await import('./saga/checkoutSaga');
    inject('checkout/watchCheckout', checkout.watchCheckout);
    inject('checkout/watchVatValidation', checkout.watchVatValidation);
    inject('checkout/watchPlaceOrder', checkout.watchplaceOrder);
    inject('checkout/watchPlaceOrderCard', checkout.watchPlaceOrderCard);
  },
  thankYou: async () => {
    const { watchThankYou } = await import('./saga/thankyouSaga');
    inject('thankYou/watchThankYou', watchThankYou);
  },
  contact: async () => {
    const { watchContactPage } = await import('./saga/contactPageSaga');
    inject('static/watchContactPage', watchContactPage);
  },
  dataProtection: async () => {
    const { watchDataProtectionPage } = await import('./saga/dataProtectionPageSaga');
    inject('static/watchDataProtectionPage', watchDataProtectionPage);
  },
  terms: async () => {
    const { watchTermsConditionsPage } = await import('./saga/TermsConditionsPageSaga');
    inject('static/watchTermsConditionsPage', watchTermsConditionsPage);
  },
  gallery: async () => {
    const { watchGalleryCollectionPage } = await import('./saga/GalleryCollectionPageSaga');
    inject('static/watchGalleryCollectionPage', watchGalleryCollectionPage);
  },
  lab: async () => {
    const { watchOurLabsPage } = await import('./saga/OurLabsPageSaga');
    inject('static/watchOurLabsPage', watchOurLabsPage);
  },
  frames: async () => {
    const { watchFramesPage } = await import('./saga/FramesPageSaga');
    inject('static/watchFramesPage', watchFramesPage);
  },
  quality: async () => {
    const { watchQualityPage } = await import('./saga/QualityPageSaga');
    inject('static/watchQualityPage', watchQualityPage);
  },
  orderDetail: async () => {
    const { watchOrderDetail } = await import('./saga/OrderDetailSaga');
    inject('account/watchOrderDetail', watchOrderDetail);
  },
  creditSlips: async () => {
    const { watchCreditSlips } = await import('./saga/CreditSlipSaga');
    inject('account/watchCreditSlips', watchCreditSlips);
  },
  wishlist: async () => {
    const wishlist = await import('./saga/wishlistSaga');
    inject('wishlist/watchAddToWishlist', wishlist.watchAddToWishlist);
    inject('wishlist/watchUpdateWishlist', wishlist.watchUpdateWishlist);
  },
};

export function ensureSagaGroup(key) {
  if (!key || !sagaGroups[key]) return Promise.resolve();
  if (!loadedGroups.has(key)) {
    loadedGroups.set(key, sagaGroups[key]());
  }
  return loadedGroups.get(key);
}

export function ensureSagaGroups(keys = []) {
  return Promise.all(keys.map(ensureSagaGroup));
}

export function getSagaGroupsForPath(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (['en', 'nl', 'de'].includes(segments[0])) segments.shift();

  const route = segments[0] || '';
  if (segments.some(segment => segment.endsWith('.html'))) return ['product'];

  const groupsByRoute = {
    shop: ['listing'],
    cart: ['cart'],
    checkout: ['checkout'],
    'thank-you': ['thankYou'],
    'contact-us': ['contact'],
    'Data_Protection-Privacy_Policy': ['dataProtection'],
    'terms-conditions': ['terms'],
    GettyImagesGallerycollection: ['gallery'],
    'our-lab': ['lab'],
    frames: ['frames'],
    'quality-prints': ['quality'],
    order: ['orderDetail'],
    'credit-slips': ['creditSlips'],
    wishlist: ['wishlist'],
  };

  if (groupsByRoute[route]) return groupsByRoute[route];
  if (!route) return [];

  const nonListingRoutes = new Set([
    'signup', 'account', 'my-orders', 'sitemap', 'my-vouchers',
    'my-addresses', 'edit-address', 'add-address', 'reset-password',
    'order-cancelled', 'page',
  ]);
  return nonListingRoutes.has(route) ? [] : ['listing'];
}
